import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Table,
  Button,
  Input,
  Modal,
  Form,
  Select,
  InputNumber,
  Tag,
  Space,
  Typography,
  message,
  Row,
  Col,
  Card,
  DatePicker,
  Alert,
  Divider,
  Descriptions,
  Badge,
  Tooltip,
  Statistic
} from 'antd';
import {
  PlusOutlined,
  SearchOutlined,
  ReloadOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  FilterOutlined,
  PrinterOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { useReactToPrint } from 'react-to-print';
import { movimientosService, productosService } from '../services/api';
import { useAuth } from '../context/AuthContext';
import MovimientosPrint from '../components/print/MovimientosPrint';

const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;
const { RangePicker } = DatePicker;

const MOTIVOS = [
  'Compra',
  'Venta',
  'Préstamo',
  'Devolución'
];

const formatCurrency = (amount) => {
  if (!amount || parseFloat(amount) === 0) return '—';
  return new Intl.NumberFormat('es-MX', {
    style: 'currency',
    currency: 'MXN',
    minimumFractionDigits: 2
  }).format(amount);
};

function Movimientos() {
  const { user } = useAuth();
  const canCreate = user?.rol === 'root' || user?.rol === 'admin';

  const [movimientos, setMovimientos]         = useState([]);
  const [productos, setProductos]             = useState([]);
  const [loading, setLoading]                 = useState(false);
  const [modalOpen, setModalOpen]             = useState(false);
  const [tipoMovimiento, setTipoMovimiento]   = useState('entrada');
  const [saving, setSaving]                   = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [filters, setFilters]                 = useState({ search: '', tipo: '', fechas: null });
  const [printData, setPrintData]             = useState(null);
  const [printLoading, setPrintLoading]       = useState(false);
  const [form] = Form.useForm();

  const printRef = useRef(null);

  const triggerPrint = useReactToPrint({
    content: () => printRef.current,
    documentTitle: 'Movimientos - Inventario',
    pageStyle: `@page { size: A4 landscape; margin: 1cm; } body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }`,
    onAfterPrint: () => {
      setPrintData(null);
      setPrintLoading(false);
    },
  });

  // ── Fetch de movimientos ─────────────────────────────────────────────────
  const fetchMovimientos = useCallback(async (filterParams = {}) => {
    setLoading(true);
    try {
      const params = {};
      if (filterParams.search)  params.search = filterParams.search;
      if (filterParams.tipo)    params.tipo   = filterParams.tipo;
      if (filterParams.fechas && filterParams.fechas[0]) {
        params.fecha_inicio = filterParams.fechas[0].format('YYYY-MM-DD');
        params.fecha_fin    = filterParams.fechas[1].format('YYYY-MM-DD');
      }
      const res = await movimientosService.getAll(params);
      setMovimientos(res.data.data || []);
    } catch (error) {
      message.error(error.displayMessage || 'Error al cargar movimientos');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchProductos = useCallback(async () => {
    try {
      const res = await productosService.getAll({ activo: 1 });
      setProductos(res.data.data || []);
    } catch {
      // silent
    }
  }, []);

  useEffect(() => {
    fetchMovimientos();
    fetchProductos();
  }, [fetchMovimientos, fetchProductos]);

  // ── Efecto: cuando printData está listo, disparar impresión ─────────────
  useEffect(() => {
    if (printData === null) return;
    triggerPrint();
  }, [printData]); // eslint-disable-line

  // ── Imprimir: obtiene TODOS los registros con filtros actuales ───────────
  const handlePrint = async () => {
    setPrintLoading(true);
    try {
      const params = {};
      if (filters.search)  params.search = filters.search;
      if (filters.tipo)    params.tipo   = filters.tipo;
      if (filters.fechas && filters.fechas[0]) {
        params.fecha_inicio = filters.fechas[0].format('YYYY-MM-DD');
        params.fecha_fin    = filters.fechas[1].format('YYYY-MM-DD');
      }
      const res = await movimientosService.getAll(params);
      setPrintData(res.data.data || []);
    } catch (error) {
      message.error('Error al preparar la impresión');
      setPrintLoading(false);
    }
  };

  // ── Filtros ───────────────────────────────────────────────────────────────
  const applyFilters = () => fetchMovimientos(filters);
  const clearFilters = () => {
    const cleared = { search: '', tipo: '', fechas: null };
    setFilters(cleared);
    fetchMovimientos(cleared);
  };

  // ── Modal ─────────────────────────────────────────────────────────────────
  const openModal = (tipo) => {
    setTipoMovimiento(tipo);
    setSelectedProduct(null);
    form.resetFields();
    form.setFieldValue('tipo', tipo);
    setModalOpen(true);
  };

  const handleProductoChange = (productoId) => {
    const prod = productos.find((p) => p.id === productoId);
    setSelectedProduct(prod || null);
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      values.tipo = tipoMovimiento;

      if (tipoMovimiento === 'salida' && selectedProduct) {
        const stockActual = parseFloat(selectedProduct.stock_actual);
        if (parseFloat(values.cantidad) > stockActual) {
          message.error(
            `Stock insuficiente. Disponible: ${stockActual.toLocaleString('es-MX')} ${selectedProduct.unidad_medida}`
          );
          return;
        }
      }

      setSaving(true);
      await movimientosService.create(values);
      message.success(
        `${tipoMovimiento === 'entrada' ? 'Entrada' : 'Salida'} registrada exitosamente`
      );
      setModalOpen(false);
      fetchMovimientos(filters);
      fetchProductos();
    } catch (error) {
      if (error.errorFields) return;
      message.error(error.displayMessage || 'Error al registrar movimiento');
    } finally {
      setSaving(false);
    }
  };

  const getRowClassName = (record) =>
    record.tipo === 'entrada' ? 'row-entrada' : 'row-salida';

  // ── Columnas de la tabla ─────────────────────────────────────────────────
  const columns = [
    {
      title: 'Fecha',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 140,
      sorter: (a, b) => new Date(a.created_at) - new Date(b.created_at),
      defaultSortOrder: 'descend',
      render: (val) => (
        <div>
          <div style={{ fontSize: 13 }}>{dayjs(val).format('DD/MM/YYYY')}</div>
          <Text type="secondary" style={{ fontSize: 11 }}>{dayjs(val).format('HH:mm:ss')}</Text>
        </div>
      )
    },
    {
      title: 'Clave',
      key: 'producto_clave',
      width: 95,
      render: (_, record) => (
        <Text code style={{ fontSize: 12 }}>{record.producto?.clave}</Text>
      )
    },
    {
      title: 'Producto',
      key: 'producto_nombre',
      ellipsis: true,
      render: (_, record) => record.producto?.nombre
    },
    {
      title: 'Tipo',
      dataIndex: 'tipo',
      key: 'tipo',
      width: 100,
      filters: [
        { text: 'Entrada', value: 'entrada' },
        { text: 'Salida',  value: 'salida'  }
      ],
      onFilter: (value, record) => record.tipo === value,
      render: (tipo) =>
        tipo === 'entrada' ? (
          <Tag color="success" icon={<ArrowUpOutlined />}>Entrada</Tag>
        ) : (
          <Tag color="error" icon={<ArrowDownOutlined />}>Salida</Tag>
        )
    },
    {
      title: 'Cantidad',
      dataIndex: 'cantidad',
      key: 'cantidad',
      width: 110,
      align: 'right',
      render: (val, record) => (
        <Text strong>
          {parseFloat(val).toLocaleString('es-MX')} {record.producto?.unidad_medida}
        </Text>
      )
    },
    {
      title: 'Stk. Anterior',
      dataIndex: 'stock_anterior',
      key: 'stock_anterior',
      width: 110,
      align: 'right',
      render: (val) => parseFloat(val).toLocaleString('es-MX')
    },
    {
      title: 'Stk. Posterior',
      dataIndex: 'stock_posterior',
      key: 'stock_posterior',
      width: 120,
      align: 'right',
      render: (val, record) => (
        <Text strong style={{ color: record.tipo === 'entrada' ? '#52c41a' : '#ff4d4f' }}>
          {parseFloat(val).toLocaleString('es-MX')}
        </Text>
      )
    },
    {
      title: 'Precio Unit.',
      dataIndex: 'precio_unitario',
      key: 'precio_unitario',
      width: 110,
      align: 'right',
      render: (val) => formatCurrency(val)
    },
    {
      title: 'Valor Total',
      key: 'valor_total',
      width: 110,
      align: 'right',
      render: (_, record) => {
        const total = parseFloat(record.cantidad) * parseFloat(record.precio_unitario || 0);
        return total > 0 ? formatCurrency(total) : <Text type="secondary">—</Text>;
      }
    },
    {
      title: 'Motivo',
      dataIndex: 'motivo',
      key: 'motivo',
      width: 130,
      render: (val) => val ? <Tag>{val}</Tag> : <Text type="secondary">—</Text>
    },
    {
      title: 'Referencia',
      dataIndex: 'referencia',
      key: 'referencia',
      width: 120,
      render: (val) => val || <Text type="secondary">—</Text>
    },
    {
      title: 'Notas',
      dataIndex: 'notas',
      key: 'notas',
      width: 160,
      ellipsis: true,
      render: (val) => val ? (
        <Tooltip title={val}><span>{val}</span></Tooltip>
      ) : (
        <Text type="secondary">—</Text>
      )
    }
  ];

  // ─── Render ──────────────────────────────────────────────────────────────
  return (
    <div>
      {/* ── Hidden print component ───────────────────────────────────────────── */}
      <div style={{ display: 'none' }}>
        <MovimientosPrint ref={printRef} data={printData || []} filters={filters} />
      </div>

      {/* ── Encabezado ────────────────────────────────────────────────────── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <Title level={3} style={{ margin: 0 }}>Movimientos</Title>
          <Text type="secondary">Registro de entradas y salidas de inventario</Text>
        </div>
        <Space wrap>
          <Button
            icon={<PrinterOutlined />}
            onClick={handlePrint}
            loading={printLoading}
          >
            Imprimir
          </Button>
          {canCreate && (
            <Button
              type="primary"
              icon={<ArrowUpOutlined />}
              onClick={() => openModal('entrada')}
              style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }}
            >
              Nueva Entrada
            </Button>
          )}
          {canCreate && (
            <Button
              type="primary"
              icon={<ArrowDownOutlined />}
              onClick={() => openModal('salida')}
              danger
            >
              Nueva Salida
            </Button>
          )}
        </Space>
      </div>

      {/* Resumen rápido en pantalla */}
      {movimientos.length > 0 && (
        <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
          {[
            {
              title: 'Registros mostrados',
              value: movimientos.length,
              color: '#1677ff'
            },
            {
              title: 'Total entradas (uds.)',
              value: movimientos
                .filter((m) => m.tipo === 'entrada')
                .reduce((a, m) => a + parseFloat(m.cantidad), 0)
                .toLocaleString('es-MX'),
              color: '#52c41a'
            },
            {
              title: 'Total salidas (uds.)',
              value: movimientos
                .filter((m) => m.tipo === 'salida')
                .reduce((a, m) => a + parseFloat(m.cantidad), 0)
                .toLocaleString('es-MX'),
              color: '#ff4d4f'
            }
          ].map((item, i) => (
            <Col xs={8} key={i}>
              <Card size="small" bordered={false} bodyStyle={{ padding: '10px 14px' }}>
                <Statistic
                  title={item.title}
                  value={item.value}
                  valueStyle={{ color: item.color, fontSize: 20 }}
                />
              </Card>
            </Col>
          ))}
        </Row>
      )}

      {/* Filtros */}
      <Card bordered={false} style={{ marginBottom: 16 }}>
        <Row gutter={[12, 12]} align="middle">
          <Col xs={24} sm={10} md={8} lg={6}>
            <Input
              placeholder="Buscar producto, clave, motivo..."
              prefix={<SearchOutlined />}
              allowClear
              value={filters.search}
              onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))}
              onPressEnter={applyFilters}
            />
          </Col>
          <Col xs={24} sm={8} md={6} lg={4}>
            <Select
              placeholder="Tipo de movimiento"
              allowClear
              style={{ width: '100%' }}
              value={filters.tipo || undefined}
              onChange={(val) => setFilters((f) => ({ ...f, tipo: val || '' }))}
            >
              <Option value="entrada">
                <Tag color="success" icon={<ArrowUpOutlined />}>Entrada</Tag>
              </Option>
              <Option value="salida">
                <Tag color="error" icon={<ArrowDownOutlined />}>Salida</Tag>
              </Option>
            </Select>
          </Col>
          <Col xs={24} sm={12} md={10} lg={8}>
            <RangePicker
              style={{ width: '100%' }}
              format="DD/MM/YYYY"
              placeholder={['Fecha inicio', 'Fecha fin']}
              value={filters.fechas}
              onChange={(dates) => setFilters((f) => ({ ...f, fechas: dates }))}
            />
          </Col>
          <Col>
            <Button icon={<FilterOutlined />} type="primary" ghost onClick={applyFilters}>
              Filtrar
            </Button>
          </Col>
          <Col>
            <Button icon={<ReloadOutlined />} onClick={clearFilters}>
              Limpiar
            </Button>
          </Col>
        </Row>
      </Card>

      {/* Tabla paginada */}
      <Card bordered={false}>
        <div style={{ marginBottom: 8 }}>
          <Text type="secondary">{movimientos.length} movimiento(s) encontrado(s)</Text>
        </div>
        <Table
          dataSource={movimientos}
          columns={columns}
          rowKey="id"
          loading={loading}
          rowClassName={getRowClassName}
          size="small"
          scroll={{ x: 1400 }}
          pagination={{
            pageSize: 20,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50', '100'],
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} de ${total} movimientos`,
            locale: { items_per_page: '/ página' }
          }}
          locale={{ emptyText: 'No se encontraron movimientos' }}
        />
      </Card>

      {/* ── Modal Nuevo Movimiento ─────────────────────────────────────────── */}
      <Modal
        title={
          <Space>
            {tipoMovimiento === 'entrada' ? (
              <Tag color="success" icon={<ArrowUpOutlined />} style={{ fontSize: 15, padding: '4px 12px' }}>
                ENTRADA DE INVENTARIO
              </Tag>
            ) : (
              <Tag color="error" icon={<ArrowDownOutlined />} style={{ fontSize: 15, padding: '4px 12px' }}>
                SALIDA DE INVENTARIO
              </Tag>
            )}
          </Space>
        }
        open={modalOpen}
        onOk={handleSave}
        onCancel={() => setModalOpen(false)}
        okText="Registrar Movimiento"
        cancelText="Cancelar"
        confirmLoading={saving}
        okButtonProps={{
          style: tipoMovimiento === 'entrada'
            ? { backgroundColor: '#52c41a', borderColor: '#52c41a' }
            : { backgroundColor: '#ff4d4f', borderColor: '#ff4d4f' }
        }}
        width={620}
        destroyOnClose
      >
        <Divider style={{ marginTop: 8 }} />

        {selectedProduct && (
          <Alert
            type={
              tipoMovimiento === 'entrada'
                ? 'info'
                : parseFloat(selectedProduct.stock_actual) === 0
                ? 'error'
                : 'warning'
            }
            showIcon
            style={{ marginBottom: 16 }}
            message={
              <Descriptions size="small" column={{ xs: 1, sm: 2, md: 4 }}>
                <Descriptions.Item label="Stock actual">
                  <Text
                    strong
                    style={{
                      color: (() => {
                        const actual = parseFloat(selectedProduct.stock_actual);
                        const min = parseFloat(selectedProduct.stock_minimo);
                        const max = parseFloat(selectedProduct.stock_maximo) || 0;
                        if (actual === 0) return '#ff4d4f';
                        if (actual <= min) return '#faad14';
                        if (max > 0 && actual >= max) return '#722ed1';
                        return '#52c41a';
                      })()
                    }}
                  >
                    {parseFloat(selectedProduct.stock_actual).toLocaleString('es-MX')}{' '}
                    {selectedProduct.unidad_medida}
                  </Text>
                </Descriptions.Item>
                <Descriptions.Item label="Stock mínimo">
                  {parseFloat(selectedProduct.stock_minimo).toLocaleString('es-MX')}{' '}
                  {selectedProduct.unidad_medida}
                </Descriptions.Item>
                <Descriptions.Item label="Stock máximo">
                  {(() => {
                    const max = parseFloat(selectedProduct.stock_maximo) || 0;
                    return max > 0
                      ? `${max.toLocaleString('es-MX')} ${selectedProduct.unidad_medida}`
                      : <Text type="secondary">Sin límite</Text>;
                  })()}
                </Descriptions.Item>
                <Descriptions.Item label="Categoría">
                  {selectedProduct.categoria?.nombre || '—'}
                </Descriptions.Item>
              </Descriptions>
            }
          />
        )}

        <Form form={form} layout="vertical" requiredMark="optional">
          <Form.Item
            name="producto_id"
            label="Producto"
            rules={[{ required: true, message: 'Selecciona un producto' }]}
          >
            <Select
              placeholder="Busca y selecciona un producto..."
              showSearch
              allowClear
              filterOption={(input, option) => {
                const prod = productos.find((p) => p.id === option.value);
                if (!prod) return false;
                return (
                  prod.nombre.toLowerCase().includes(input.toLowerCase()) ||
                  prod.clave.toLowerCase().includes(input.toLowerCase())
                );
              }}
              onChange={handleProductoChange}
              optionLabelProp="label"
            >
              {productos.map((prod) => (
                <Option
                  key={prod.id}
                  value={prod.id}
                  label={`[${prod.clave}] ${prod.nombre}`}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Space>
                      <Text code style={{ fontSize: 11 }}>{prod.clave}</Text>
                      <span>{prod.nombre}</span>
                    </Space>
                    <Badge
                      count={`${parseFloat(prod.stock_actual).toLocaleString('es-MX')} ${prod.unidad_medida}`}
                      style={{
                        backgroundColor:
                          parseFloat(prod.stock_actual) === 0
                            ? '#ff4d4f'
                            : parseFloat(prod.stock_actual) <= parseFloat(prod.stock_minimo)
                            ? '#faad14'
                            : '#52c41a',
                        fontSize: 11
                      }}
                    />
                  </div>
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item
                name="cantidad"
                label="Cantidad"
                rules={[
                  { required: true, message: 'La cantidad es requerida' },
                  { type: 'number', min: 0.01, message: 'La cantidad debe ser mayor a 0' },
                  {
                    validator: (_, value) => {
                      if (tipoMovimiento === 'salida' && selectedProduct && value) {
                        const stockActual = parseFloat(selectedProduct.stock_actual);
                        if (parseFloat(value) > stockActual) {
                          return Promise.reject(
                            `Excede el stock disponible (${stockActual.toLocaleString('es-MX')} ${selectedProduct.unidad_medida})`
                          );
                        }
                      }
                      return Promise.resolve();
                    }
                  }
                ]}
              >
                <InputNumber
                  style={{ width: '100%' }}
                  min={0.01}
                  precision={2}
                  placeholder="0"
                  formatter={(v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                  parser={(v) => v.replace(/,/g, '')}
                  addonAfter={selectedProduct?.unidad_medida || 'UND'}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item name="precio_unitario" label="Precio Unitario">
                <InputNumber
                  style={{ width: '100%' }}
                  min={0}
                  precision={2}
                  prefix="$"
                  placeholder="0.00"
                  formatter={(v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                  parser={(v) => v.replace(/,/g, '')}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item name="motivo" label="Motivo">
                <Select placeholder="Selecciona un motivo" allowClear>
                  {MOTIVOS.map((m) => (
                    <Option key={m} value={m}>{m}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item name="referencia" label="Referencia">
                <Input placeholder="No. factura, orden, folio..." maxLength={100} />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="notas" label="Notas">
            <TextArea
              placeholder="Notas u observaciones adicionales..."
              rows={2}
              maxLength={500}
              showCount
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}

export default Movimientos;
